import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

export default function Privacy() {
  usePageMeta(
    "Privacy Policy — Hope Chest",
    "How Hope Chest collects, stores, and protects your family's caregiving information. We never sell your data."
  );
  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#3D3935]">
      <nav className="max-w-4xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="privacy-home-link">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold">Hope Chest</span>
        </Link>
        <Link to="/terms" className="text-sm text-[#7A736E] hover:text-[#3D3935]">Terms of Service →</Link>
      </nav>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-12 prose-content">
        <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Privacy Policy</p>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-6">How we care for your data.</h1>
        <p className="text-[#7A736E] mb-2">Effective date: June 11, 2026</p>
        <p className="text-[#7A736E] mb-10">Last updated: June 11, 2026</p>

        <div className="space-y-8 text-[15px] leading-relaxed">
          <section>
            <p>Hope Chest Digital Binder ("Hope Chest", "we", "our", "us") is operated by Organized Hope. We built Hope Chest to help families organize caregiving — and we treat the information you trust us with the same way we'd want our own family's information treated: privately, securely, and respectfully.</p>
            <p className="mt-3"><strong>This policy is written in plain language.</strong> If you have any questions, contact us at the address below.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">1. Who we are</h2>
            <p>Organized Hope<br/>2905 Triana Boulevard<br/>Huntsville, AL 35805<br/>Phone: (256) 537-8872<br/>Email: <a href="mailto:support@organizedhope.online" className="text-[#3A5A4A] underline">support@organizedhope.online</a></p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">2. What information we collect</h2>
            <p>You give us:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Account info</strong>: your name, email address, and (for password sign-in) a hashed password. We never store passwords in plain text.</li>
              <li><strong>Hope Chest content</strong>: names of loved ones, dates of birth, medical conditions, allergies, medications, vitals, appointments, doctor's notes, daily care log entries, expense entries, emergency contacts, and any documents or photos you upload to the Document Vault.</li>
              <li><strong>Care team info</strong>: email addresses of people you invite to a Hope Chest.</li>
              <li><strong>Billing info</strong> (if you upgrade to Premium): billing details are handled directly by Stripe; we never see your card number.</li>
              <li><strong>Calendar tokens</strong> (optional): if you connect Google Calendar, we store the OAuth refresh token so we can push planner events to your calendar. We do not read your existing calendar events.</li>
            </ul>
            <p className="mt-3">We collect automatically:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Basic technical data your browser sends (IP address, browser type) for security and abuse prevention.</li>
              <li>App usage events (page views, actions like "created medication", "uploaded document") to improve the product. We do not associate this with your medical content.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">3. How we use your information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To provide the Hope Chest service to you and the people you share it with.</li>
              <li>To send you the emails you opted into: daily digest, weekly care-team report, gentle check-in nudges, member invitations, and account/security notifications.</li>
              <li>To process payments (via Stripe) if you subscribe to Premium.</li>
              <li>To push planner events to your Google Calendar (only if you connect it).</li>
              <li>To investigate abuse, security incidents, or technical issues.</li>
              <li>To comply with the law.</li>
            </ul>
            <p className="mt-3"><strong>We do not</strong> sell your data. We do not use your medical or care information to train AI models. We do not share your data with advertisers.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">4. Who can see your data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>You</strong>, the owner of a Hope Chest.</li>
              <li><strong>People you invite</strong> to a Hope Chest, with the role you assign them (Contributor, Viewer, or Professional). You can remove them anytime.</li>
              <li><strong>Service providers</strong> we use to operate Hope Chest: Resend (email delivery), Stripe (payments), Google (calendar push, if you connect), our cloud hosting provider (Emergent / MongoDB). Each is contractually required to handle data securely.</li>
              <li><strong>Law enforcement</strong>, only when legally required (e.g., a valid subpoena).</li>
            </ul>
            <p className="mt-3"><strong>We do not</strong> give your data to insurance companies, employers, or any party not listed above.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">5. HIPAA and medical information</h2>
            <p>Hope Chest is a <strong>personal and family organization tool</strong> for individual caregivers and their loved ones. It is <strong>not</strong> a HIPAA-covered entity, and we are <strong>not</strong> providing medical advice, diagnosis, or treatment.</p>
            <p className="mt-3">If you are a healthcare professional using Hope Chest as part of your work with patients, you remain responsible for your own HIPAA compliance. We can sign a Business Associate Agreement (BAA) for Premium business accounts — contact us for details.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">6. Security</h2>
            <p>We protect your data using:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>HTTPS encryption in transit for everything between your browser and our servers.</li>
              <li>Encryption at rest for our database and uploaded files.</li>
              <li>Bcrypt-hashed passwords (we cannot recover your password — only reset it).</li>
              <li>Access control: every record is tied to a Hope Chest, and we check on every request that the person asking has permission.</li>
              <li>Regular backups.</li>
            </ul>
            <p className="mt-3">No system is perfect. If we ever become aware of a breach affecting your account, we'll notify you within 72 hours.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">7. Your rights</h2>
            <p>You can, at any time:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Access</strong> your data — it's all visible in the app, and you can export each Hope Chest as a PDF.</li>
              <li><strong>Correct</strong> any information by editing it in the app.</li>
              <li><strong>Delete</strong> a Hope Chest, which permanently removes all its content and uploaded files.</li>
              <li><strong>Delete your account</strong> — email us at hello@organizedhope.online and we'll permanently delete your account and all Hope Chests you own within 30 days.</li>
              <li><strong>Opt out</strong> of any email (daily digest, weekly report, check-in nudges) in Settings → Notifications.</li>
            </ul>
            <p className="mt-3">If you are in the EU, UK, or California, you also have rights under GDPR / UK GDPR / CCPA, including the right to a portable copy of your data and the right to object to processing. To exercise these, email hello@organizedhope.online.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">8. Children</h2>
            <p>Hope Chest is not intended for children under 13, and we do not knowingly collect data from anyone under 13. If you become aware that a child has provided data to us, please contact us and we will delete it.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">9. Cookies</h2>
            <p>We use only essential cookies needed to keep you signed in. We do not use advertising or tracking cookies.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">10. Changes to this policy</h2>
            <p>If we make material changes to this policy, we'll email you. Continuing to use Hope Chest after a change means you accept the updated policy.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">11. Contact us</h2>
            <p>Questions, concerns, or requests? We genuinely want to hear from you.</p>
            <p className="mt-3">Organized Hope<br/>2905 Triana Boulevard<br/>Huntsville, AL 35805<br/>(256) 537-8872<br/><a href="mailto:support@organizedhope.online" className="text-[#3A5A4A] underline">support@organizedhope.online</a></p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-[#EBE5DF] text-sm text-[#7A736E] flex justify-between">
          <Link to="/" className="hover:text-[#3D3935]">← Back home</Link>
          <Link to="/terms" className="hover:text-[#3D3935]">Terms of Service →</Link>
        </div>
      </main>
    </div>
  );
}
